import { AppointmentForm } from "./AppointmentForm";
import { motion } from "framer-motion";
import { Phone, MessageCircle } from "lucide-react";
import { PRACTICE } from "../../lib/data";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const BookingSection = () => {
  return (
    <section
      id="book"
      className="py-20 sm:py-28 px-6 sm:px-10 lg:px-14 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #086A75 0%, #0E9CA9 100%)",
      }}
    >
      <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-white/5 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 w-[360px] h-[360px] rounded-full bg-terra/10 blur-3xl" />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 relative">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-6 text-white"
        >
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-terra-soft mb-3">
            Schedule Privately
          </p>
          <h2 className="font-sans font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Schedule Your Consultation with Dr. Venkat
          </h2>
          <p className="mt-5 max-w-lg text-base sm:text-lg text-white/80">
            Take the first step towards expert urological care. Book your appointment today — Dr. Venkat's team responds within a few hours.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${PRACTICE.phone.replace(/\s/g, "")}`}
              data-testid="book-call-btn"
              className="inline-flex items-center gap-2 bg-white text-forest hover:bg-bone-2 px-6 py-3.5 rounded-full text-sm font-semibold transition"
            >
              <Phone size={16} /> Call Now
            </a>
            <a
              href={`https://wa.me/${PRACTICE.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-whatsapp-btn"
              className="inline-flex items-center gap-2 border-1.5 border-white text-white hover:bg-white hover:text-forest px-6 py-3.5 rounded-full text-sm font-semibold transition"
              style={{ borderWidth: 1.5 }}
            >
              <MessageCircle size={16} /> WhatsApp Now
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-5 max-w-md">
            <Mini value="< 4 hrs" label="Response time" />
            <Mini value="100%" label="Confidential" />
            <Mini value="3" label="Languages" />
            <Mini value="24/7" label="Emergency line" />
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15 }}
          className="lg:col-span-6"
        >
          <div className="bg-paper rounded-3xl p-8 sm:p-10 shadow-2xl">
            <h3 className="font-sans font-bold text-2xl text-ink text-center mb-1">
              Book Your Appointment
            </h3>
            <p className="text-center text-sm text-ink-soft mb-6">
              Dr. Venkat's team will contact you shortly
            </p>
            <AppointmentForm variant="light" testidPrefix="book" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Mini = ({ value, label }) => (
  <div className="border-l-2 border-white/30 pl-4">
    <p className="font-sans font-extrabold text-2xl text-white leading-none">{value}</p>
    <p className="mt-1.5 text-[11px] tracking-[0.16em] uppercase text-white/60">{label}</p>
  </div>
);
